/**
 * Auto-hiding scrollbars — the thumb appears while you scroll and fades out
 * once you stop, instead of sitting in the gutter permanently.
 *
 * Browsers already come in two flavours here, and the split is what this
 * module exists to detect:
 *
 *  - *Overlay* scrollbars (macOS set to "Show scroll bars: when scrolling",
 *    every touch device) already fade on their own. Touching
 *    `::-webkit-scrollbar` on those platforms would be a downgrade, not an
 *    upgrade: any custom scrollbar rule switches the element back to a
 *    classic, space-taking scrollbar. So we leave them alone entirely.
 *  - *Classic* scrollbars (Windows, most Linux, macOS set to "Always") are
 *    the permanent ones the fade is meant to replace.
 *
 * The CSS that draws the fade lives in `src/styles.css` and is gated behind
 * `.gs-classic-scrollbars`, which this module puts on <html> only in the
 * second case. `.gs-scrolling` is the moment-to-moment signal: on while a
 * scroll is in flight, off once one has been idle for {@link
 * SCROLLBAR_HIDE_DELAY_MS}.
 *
 * Only the thumb's colour animates — the gutter stays reserved at all times.
 * Fading the scrollbar's *width* would reflow the whole page on every scroll.
 */

/** On <html> when this platform draws classic (space-taking) scrollbars. */
export const CLASSIC_SCROLLBARS_CLASS = "gs-classic-scrollbars";

/** On <html> while a scroll is in flight; the CSS paints the thumb from it. */
export const SCROLLING_CLASS = "gs-scrolling";

/** How long the thumb lingers after the last scroll event. */
export const SCROLLBAR_HIDE_DELAY_MS = 900;

/**
 * Width of the gutter a scrollbar reserves, in px — 0 when the browser draws
 * overlay scrollbars, which is exactly the case we must not touch.
 *
 * Measured rather than sniffed: the answer depends on an OS preference and on
 * whether a mouse is attached, neither of which a user-agent string carries.
 */
function measureScrollbarGutter(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;";
  document.body.appendChild(probe);
  const gutter = probe.offsetWidth - probe.clientWidth;
  probe.remove();
  return gutter;
}

/**
 * Installs the auto-hide behaviour for the lifetime of the returned disposer.
 *
 * @param options.classic Override the platform probe. Tests pass it because
 *   jsdom lays nothing out and so always measures a 0px gutter.
 * @returns A disposer that removes both classes, the pending timer and the
 *   listeners.
 */
export function initAutoHideScrollbars(options?: {
  classic?: boolean;
}): () => void {
  const classic = options?.classic ?? measureScrollbarGutter() > 0;
  if (!classic) return () => {};

  const root = document.documentElement;
  root.classList.add(CLASSIC_SCROLLBARS_CLASS);

  let timer: ReturnType<typeof setTimeout> | undefined;

  const onScroll = () => {
    root.classList.add(SCROLLING_CLASS);
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      root.classList.remove(SCROLLING_CLASS);
    }, SCROLLBAR_HIDE_DELAY_MS);
  };

  // Capture phase, because `scroll` does not bubble: this single registration
  // is what lets one listener cover the page *and* every inner scroll
  // container (the dashboard's panes, tables, dropdowns) at once.
  document.addEventListener("scroll", onScroll, {
    capture: true,
    passive: true,
  });

  return () => {
    document.removeEventListener("scroll", onScroll, { capture: true });
    if (timer !== undefined) clearTimeout(timer);
    root.classList.remove(SCROLLING_CLASS, CLASSIC_SCROLLBARS_CLASS);
  };
}
